// lib/supabase.js
// -----------------------------------------------------------------------------
// MOCK Supabase client.
//
// Supabase has been dropped for the new version. Rather than touch all 52 files
// that import this module, we expose a drop-in mock that mimics the pieces of
// the supabase-js API the app uses (auth, from().select()..., rpc, channel,
// storage) and serves seeded fake data from lib/mockData.js.
//
// Everything runs in-memory — no network. Writes persist for the session.
// To restore real Supabase later, revert this file to the createClient version.
// -----------------------------------------------------------------------------

import { createClient } from '@supabase/supabase-js';
import { tables, rpcResults, currentAuthUser, currentSession } from './mockData';

const ok = (data, extra = {}) => ({ data, error: null, status: 200, statusText: 'OK', ...extra });

let idCounter = 1;
const genId = (table) => `${table}-mock-${Date.now()}-${idCounter++}`;

// A chainable, thenable query builder that reads/writes the in-memory tables.
class MockQueryBuilder {
  constructor(table, preset) {
    this.table = table;
    this.preset = preset; // when set (rpc), resolve against this dataset, not a table
    this.filters = [];       // { op, col, val }
    this.orderBy = null;
    this.limitN = null;
    this.rangeFromTo = null;
    this.wantSingle = false;
    this.wantMaybeSingle = false;
    this.write = null;       // { type: 'insert'|'update'|'delete'|'upsert', payload }
    this.wantSelect = false;
    this.countMode = null;
    this.headOnly = false;
  }

  // ---- filters -------------------------------------------------------------
  eq(col, val) { this.filters.push({ op: 'eq', col, val }); return this; }
  neq(col, val) { this.filters.push({ op: 'neq', col, val }); return this; }
  in(col, val) { this.filters.push({ op: 'in', col, val }); return this; }
  is(col, val) { this.filters.push({ op: 'is', col, val }); return this; }
  // Loosely-supported filters (kept as no-ops so chains don't break).
  // NOTE: `ilike`/`or`/`like` don't filter here, so text search (searchService)
  // returns ALL seeded rows under the mock. This is a MOCK-ONLY artifact — the
  // real queries use valid PostgREST `.or(col.ilike.%q%,...)` filters that a live
  // Supabase backend honors, so search filters correctly once the DB is connected.
  gt() { return this; }
  gte() { return this; }
  lt() { return this; }
  lte() { return this; }
  like() { return this; }
  ilike() { return this; }
  or() { return this; }
  not() { return this; }
  filter() { return this; }
  match(obj) { Object.entries(obj || {}).forEach(([col, val]) => this.filters.push({ op: 'eq', col, val })); return this; }
  contains() { return this; }
  overlaps() { return this; }
  containedBy() { return this; }
  textSearch() { return this; }

  // ---- shaping -------------------------------------------------------------
  select(_cols, opts = {}) {
    this.wantSelect = true;
    if (opts && opts.count) this.countMode = opts.count;
    if (opts && opts.head) this.headOnly = true;
    return this;
  }
  order(col, opts = {}) { this.orderBy = { col, ascending: opts.ascending !== false }; return this; }
  limit(n) { this.limitN = n; return this; }
  range(from, to) { this.rangeFromTo = [from, to]; return this; }
  single() { this.wantSingle = true; return this; }
  maybeSingle() { this.wantMaybeSingle = true; return this; }
  csv() { return this; }
  abortSignal() { return this; }

  // ---- writes --------------------------------------------------------------
  insert(payload) { this.write = { type: 'insert', payload }; return this; }
  upsert(payload) { this.write = { type: 'upsert', payload }; return this; }
  update(payload) { this.write = { type: 'update', payload }; return this; }
  delete() { this.write = { type: 'delete' }; return this; }

  // ---- execution -----------------------------------------------------------
  _store() {
    if (!tables[this.table]) tables[this.table] = [];
    return tables[this.table];
  }

  _matches(row) {
    return this.filters.every(({ op, col, val }) => {
      const cell = row[col];
      switch (op) {
        case 'eq': return cell === val;
        case 'neq': return cell !== val;
        case 'in': return Array.isArray(val) && val.includes(cell);
        case 'is': return cell === val || (val === null && (cell === null || cell === undefined));
        default: return true;
      }
    });
  }

  _applyWrite(store) {
    const { type, payload } = this.write;
    if (type === 'insert' || type === 'upsert') {
      const incoming = Array.isArray(payload) ? payload : [payload];
      const inserted = incoming.map((r) => ({ id: r.id || genId(this.table), created_at: new Date().toISOString(), ...r }));
      store.push(...inserted);
      return inserted;
    }
    if (type === 'update') {
      const affected = [];
      store.forEach((row, i) => {
        if (this._matches(row)) {
          store[i] = { ...row, ...payload, updated_at: new Date().toISOString() };
          affected.push(store[i]);
        }
      });
      return affected;
    }
    if (type === 'delete') {
      const affected = [];
      for (let i = store.length - 1; i >= 0; i--) {
        if (this._matches(store[i])) affected.push(store.splice(i, 1)[0]);
      }
      return affected;
    }
    return [];
  }

  _resolve() {
    try {
      // rpc() / preset mode: filter+order+limit a fixed dataset when it's an array
      if (this.preset !== undefined) {
        let rows = this.preset;
        if (Array.isArray(rows)) {
          rows = rows.filter((r) => this._matches(r));
          if (this.orderBy) {
            const { col, ascending } = this.orderBy;
            rows = [...rows].sort((a, b) => (a[col] === b[col] ? 0 : (a[col] > b[col] ? 1 : -1) * (ascending ? 1 : -1)));
          }
          if (this.rangeFromTo) rows = rows.slice(this.rangeFromTo[0], this.rangeFromTo[1] + 1);
          if (this.limitN != null) rows = rows.slice(0, this.limitN);
        }
        const count = this.countMode ? (Array.isArray(rows) ? rows.length : 1) : null;
        if (this.wantSingle) return rows && rows[0] ? ok(rows[0], { count }) : ok(Array.isArray(rows) ? null : rows, { count });
        if (this.wantMaybeSingle) return ok(Array.isArray(rows) ? (rows[0] ?? null) : rows, { count });
        return ok(rows, { count });
      }

      const store = this._store();
      let rows;

      if (this.write) {
        rows = this._applyWrite(store);
        // A write without .select() returns no rows in supabase-js
        if (!this.wantSelect) rows = null;
      } else {
        rows = store.filter((r) => this._matches(r));
        if (this.orderBy) {
          const { col, ascending } = this.orderBy;
          rows = [...rows].sort((a, b) => {
            const av = a[col], bv = b[col];
            if (av === bv) return 0;
            const cmp = av > bv ? 1 : -1;
            return ascending ? cmp : -cmp;
          });
        }
        if (this.rangeFromTo) rows = rows.slice(this.rangeFromTo[0], this.rangeFromTo[1] + 1);
        if (this.limitN != null) rows = rows.slice(0, this.limitN);
      }

      const count = this.countMode ? (Array.isArray(rows) ? rows.length : store.length) : null;

      if (this.wantSingle) {
        const row = Array.isArray(rows) ? rows[0] : rows;
        if (!row) return { data: null, error: { code: 'PGRST116', message: 'Results contain 0 rows' }, count };
        return ok(row, { count });
      }
      if (this.wantMaybeSingle) {
        const row = Array.isArray(rows) ? rows[0] : rows;
        return ok(row ?? null, { count });
      }
      if (this.headOnly) return ok(null, { count });
      return ok(rows ?? [], { count });
    } catch (err) {
      return { data: null, error: { message: err.message || 'mock query error' }, count: null };
    }
  }

  // Thenable: makes `await builder` / Promise.race([...]) work.
  then(onFulfilled, onRejected) {
    return Promise.resolve(this._resolve()).then(onFulfilled, onRejected);
  }
  catch(onRejected) { return this.then(undefined, onRejected); }
  finally(cb) { return this.then((v) => { cb && cb(); return v; }, (e) => { cb && cb(); throw e; }); }
}

// ---- realtime channels (no-op) ---------------------------------------------
const makeChannel = () => {
  const channel = {
    on: () => channel,
    // Fire the status callback ASYNCHRONOUSLY, mirroring real supabase-js.
    // App code does `const ch = supabase.channel(k).subscribe(s => {...ch...})`,
    // so a synchronous callback would hit `ch` in its temporal dead zone.
    subscribe: (cb) => {
      if (typeof cb === 'function') setTimeout(() => { try { cb('SUBSCRIBED'); } catch (_) {} }, 0);
      return channel;
    },
    unsubscribe: () => Promise.resolve({ error: null }),
    send: () => Promise.resolve('ok'),
    track: () => Promise.resolve({ error: null }),
    untrack: () => Promise.resolve({ error: null }),
    presenceState: () => ({}),
    state: 'joined'
  };
  return channel;
};

// ---- storage (no-op) --------------------------------------------------------
const makeStorageBucket = () => ({
  upload: async (path) => ok({ path }),
  update: async (path) => ok({ path }),
  remove: async () => ok([]),
  download: async () => ok(new Blob()),
  list: async () => ok([]),
  createSignedUrl: async (path) => ok({ signedUrl: `mock://signed/${path}` }),
  getPublicUrl: (path) => ({ data: { publicUrl: `mock://public/${path}` } })
});

// ---- rpc --------------------------------------------------------------------
// supabase-js rpc() returns a thenable, filterable builder (e.g.
// supabase.rpc('fn').eq(...).order(...).limit(...)). Return a MockQueryBuilder
// seeded with the fake result so chaining works and `await` resolves it.
// Default for unseeded functions: [] (safe for .map/.length/optional chaining).
const runRpc = (fn) => {
  const preset = Object.prototype.hasOwnProperty.call(rpcResults, fn) ? rpcResults[fn] : [];
  return new MockQueryBuilder(null, preset);
};

// ---- auth -------------------------------------------------------------------
const authListeners = new Set();
const auth = {
  getSession: async () => ok({ session: currentSession }),
  getUser: async () => ok({ user: currentAuthUser }),
  onAuthStateChange: (callback) => {
    authListeners.add(callback);
    return { data: { subscription: { unsubscribe: () => authListeners.delete(callback) } } };
  },
  signInWithOAuth: async () => ok({ provider: 'discord', url: null }),
  signInWithPassword: async () => ok({ user: currentAuthUser, session: currentSession }),
  signOut: async () => { authListeners.forEach((cb) => { try { cb('SIGNED_OUT', null); } catch (_) {} }); return { error: null }; },
  refreshSession: async () => ok({ session: currentSession, user: currentAuthUser }),
  setSession: async () => ok({ session: currentSession, user: currentAuthUser })
};

// ---- the mock client --------------------------------------------------------
const mockSupabase = {
  from: (table) => new MockQueryBuilder(table),
  rpc: (fn) => runRpc(fn),
  channel: () => makeChannel(),
  removeChannel: () => Promise.resolve({ error: null }),
  removeAllChannels: () => Promise.resolve({ error: null }),
  getChannels: () => [],
  storage: { from: () => makeStorageBucket() },
  auth,
  // Some code uses supabase.raw() for SQL expressions; keep it non-crashing.
  raw: (sql) => ({ __raw: sql })
};

// ---- real vs mock selection -------------------------------------------------
// Use the real Supabase client when a URL + anon key are configured (and the
// mock isn't force-enabled). Otherwise fall back to the in-memory mock so the
// app still boots with no backend.
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const FORCE_MOCK = process.env.REACT_APP_USE_MOCK === 'true';
const USE_REAL = !FORCE_MOCK && !!SUPABASE_URL && !!SUPABASE_ANON_KEY;

export const supabase = USE_REAL
  ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true }
    })
  : mockSupabase;

if (typeof window !== 'undefined') {
  // eslint-disable-next-line no-console
  console.info(`[supabase] using ${USE_REAL ? 'REAL' : 'MOCK'} data layer`);
}

// -----------------------------------------------------------------------------
// Helpers re-exported for compatibility with the previous module.
// -----------------------------------------------------------------------------
export const handleSupabaseError = (error, context = '') => {
  console.error(`Supabase error ${context}:`, error);
  return {
    success: false,
    error: (error && error.message) || 'An unexpected error occurred'
  };
};

export const createRealtimeSubscription = () => makeChannel().subscribe();

export default supabase;
